import * as React from 'react';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import {styled, useTheme} from '@mui/material/styles';
import registerAccountLang from '@practicegenius/register-account/src/lang';
import {differenceInYears} from 'date-fns';
import {Button, Card, CardContent} from '@mui/material';
import {RegisterEmailScreenType} from '@practicegenius/register-account/src/screens/RegisterEmail';
import {Typography} from '@practicegenius/pg-ui/src/components/ResponsiveTypography';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import {loginBottomText} from '../components/layout';
import {ProgressScreen} from '../../../components/screens/BaseScreen';
import {EmailInput} from '../../../shared/Material/components/EmailFieldInput';

export const EmailChoiceCard = styled(Card)(({theme}) => ({
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  marginBottom: theme.spacing(2),
  height: '125px',
  '&:hover': {
    background: theme.palette.grey[100],
  },
}));

export const SwapEmailButton = styled(Button)(({theme}) => ({
  padding: theme.spacing(2, 3),
  marginTop: theme.spacing(5),
  [theme.breakpoints.down('xs')]: {
    width: '100%',
  },
}));

type EmailViewType = 'chooseEmail' | 'email' | 'responsibleEmail';

const getStartingView = (birthdate: Date) => {
  const yearsOld = differenceInYears(birthdate, new Date()) * -1;
  if (yearsOld < 13) return 'responsibleEmail';
  if (yearsOld >= 13 && yearsOld < 18) return 'chooseEmail';
  return 'email';
};

export const RegisterEmail: RegisterEmailScreenType = ({
  lang,
  onFormSubmit,
  progress,
  actionButtons,
  emailInputProps,
  setUseResponsibleEmail,
  firstName,
  birthDate,
  onEmailSwap,
}) => {
  const startingView = getStartingView(birthDate as Date) ?? 'chooseEmail';
  const [currentView, setCurrentView] =
    React.useState<EmailViewType>(startingView);
  const isTeenagerRegistration = startingView === 'chooseEmail';

  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);

  // autofocus the text input on error for easier changing of email
  React.useEffect(() => {
    if (emailInputProps.error) {
      inputRef.current?.select();
    }
  }, [emailInputProps.error]);

  const primaryButtonProps = {
    ...actionButtons.primary,
  };
  if (currentView === 'chooseEmail') {
    primaryButtonProps.disabled = true;
  }
  return (
    <form onSubmit={onFormSubmit}>
      <ProgressScreen
        helmetTitle={lang.pageTitle}
        headerTitle={lang.headerTitle}
        title={IMT(lang.title[currentView], {
          firstName,
        })}
        instructions={lang.instructions[currentView]}
        progress={progress}
        actionButtons={[primaryButtonProps, actionButtons.secondary]}
        cancelProps={{href: '/'}}
        bottomText={loginBottomText}
      >
        {currentView !== 'chooseEmail' && (
          <EmailInput
            inputRef={inputRef}
            name={emailInputProps.name}
            value={emailInputProps.value ?? ''}
            onChange={e => {
              emailInputProps.onChange(emailInputProps.name, e.target.value);
            }}
            onBlur={emailInputProps.onBlur}
            label={lang.emailInput.label[emailInputProps.name]}
            icon={EmailOutlined}
            error={emailInputProps.error}
            autoFocus
          />
        )}
        {/*  only display swap buttons for teenagers who can choose between parent or personal email */}
        {currentView === 'responsibleEmail' && isTeenagerRegistration && (
          <div>
            <SwapEmailButton
              onClick={() => {
                setCurrentView('email');
                setUseResponsibleEmail(false);
              }}
              color="inherit"
              startIcon={
                <PeopleOutlineOutlined htmlColor="rgba(0, 0, 0, 0.87)" />
              }
              variant="outlined"
            >
              <Typography
                style={{color: 'rgba(0, 0, 0, 0.87)'}}
                variant="subtitle"
              >
                {lang.buttons.swapEmail.toEmail}
              </Typography>
            </SwapEmailButton>
          </div>
        )}
        {currentView === 'email' && isTeenagerRegistration && (
          <div>
            <SwapEmailButton
              onClick={() => {
                setCurrentView('responsibleEmail');
                setUseResponsibleEmail(true);
                // when the "email already in use" error is shown and the user swaps to use
                // responsible email, prefill responsible email with personal email
                if (
                  emailInputProps.error ===
                  registerAccountLang.general.registrationError
                    .emailAlreadyInUse
                ) {
                  onEmailSwap();
                }
              }}
              color="inherit"
              startIcon={
                <PeopleOutlineOutlined htmlColor="rgba(0, 0, 0, 0.87)" />
              }
              variant="outlined"
            >
              <Typography
                style={{color: 'rgba(0, 0, 0, 0.87)'}}
                variant="subtitle"
              >
                {lang.buttons.swapEmail.toResponsibleEmail}
              </Typography>
            </SwapEmailButton>
          </div>
        )}

        {currentView === 'chooseEmail' && (
          <div>
            <EmailChoiceCard
              onClick={() => {
                setCurrentView('responsibleEmail');
                setUseResponsibleEmail(true);
                // Wipe out the other email field that won't be used
                emailInputProps.onChange('email', '');
              }}
              variant="outlined"
              title={lang.chooseEmail.responsibleEmail.cardTitle}
            >
              <PeopleOutlineOutlined
                htmlColor="rgba(0,0,0,.6)"
                fontSize="large"
                style={{marginLeft: theme.spacing(1)}}
              />
              <CardContent>
                <Typography variant="subsectionTitle">
                  {lang.chooseEmail.responsibleEmail.cardTitle}
                </Typography>
                <Typography variant="secondaryBody">
                  {lang.chooseEmail.responsibleEmail.cardBody}
                </Typography>
              </CardContent>
            </EmailChoiceCard>
            <EmailChoiceCard
              onClick={() => {
                setCurrentView('email');
                setUseResponsibleEmail(false);
                // Wipe out the other email field that won't be used
                emailInputProps.onChange('responsibleEmail', '');
              }}
              variant="outlined"
              title={lang.chooseEmail.email.cardTitle}
            >
              <PersonOutlineOutlined
                htmlColor="rgba(0,0,0,.6)"
                fontSize="large"
                style={{marginLeft: theme.spacing(1)}}
              />
              <CardContent>
                <Typography variant="subsectionTitle">
                  {lang.chooseEmail.email.cardTitle}
                </Typography>
                <Typography variant="secondaryBody">
                  {lang.chooseEmail.email.cardBody}
                </Typography>
              </CardContent>
            </EmailChoiceCard>
          </div>
        )}
      </ProgressScreen>
    </form>
  );
};
